import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Info } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

interface DisableTerraceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  establishmentId: string;
  onConfirm: () => void;
}

interface TerraceTable {
  id: string;
  name: string;
  capacity: number;
  type: 'table' | 'combined';
}

interface TerraceReservation {
  id: string;
  customerName: string;
  date: string;
  startTime: string;
  numberOfPeople: number;
}

export function DisableTerraceDialog({ open, onOpenChange, establishmentId, onConfirm }: DisableTerraceDialogProps) {
  const [tables, setTables] = useState<TerraceTable[]>([]);
  const [reservations, setReservations] = useState<TerraceReservation[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);

    const today = new Date().toISOString().split('T')[0];

    Promise.all([
      supabase
        .from('tables')
        .select('id, name, capacity')
        .eq('establishment_id', establishmentId)
        .eq('is_terrace', true),
      supabase
        .from('combined_tables')
        .select('id, name, total_capacity')
        .eq('establishment_id', establishmentId)
        .eq('is_terrace', true),
      supabase
        .from('reservations')
        .select('id, date, start_time, number_of_people, table_id, combined_table_id, terrace_preference, customers(first_name, last_name), tables(is_terrace), combined_tables(is_terrace)')
        .eq('establishment_id', establishmentId)
        .gte('date', today)
        .in('status', ['planifié', 'en_attente'])
        .order('date', { ascending: true })
        .order('start_time', { ascending: true })
        .limit(1000),
    ]).then(([tablesRes, combinedRes, reservationsRes]) => {
      const t: TerraceTable[] = [
        ...((tablesRes.data as any[]) || []).map((t: any) => ({ id: t.id, name: t.name, capacity: t.capacity, type: 'table' as const })),
        ...((combinedRes.data as any[]) || []).map((t: any) => ({ id: t.id, name: t.name, capacity: t.total_capacity, type: 'combined' as const })),
      ];
      setTables(t);

      // Choix terrasse OU placée sur une table/combinaison terrasse.
      const r: TerraceReservation[] = ((reservationsRes.data as any[]) || [])
        .filter((r: any) => r.terrace_preference === 'terrace' || r.tables?.is_terrace === true || r.combined_tables?.is_terrace === true)
        .map((r: any) => ({
        id: r.id,
        customerName: r.customers ? `${r.customers.first_name} ${r.customers.last_name}` : 'Client',
        date: r.date,
        startTime: r.start_time,
        numberOfPeople: r.number_of_people,
      }));
      setReservations(r);
      setLoading(false);
    });
  }, [open, establishmentId]);

  const hasImpact = tables.length > 0 || reservations.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-orange-500" />
            Désactiver la terrasse
          </DialogTitle>
          <DialogDescription>
            Vérifiez l'impact avant de désactiver la préférence terrasse.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <p className="text-sm text-muted-foreground py-4 text-center">Chargement...</p>
        ) : !hasImpact ? (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/50 text-sm text-muted-foreground">
            <Info className="w-4 h-4 mt-0.5 shrink-0" />
            <span>Aucun impact détecté. Aucune table terrasse ni réservation terrasse active.</span>
          </div>
        ) : (
          <div className="space-y-4">
            {tables.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-2">
                  {tables.length} table{tables.length > 1 ? 's' : ''} terrasse ne seront plus proposées en ligne (l'équipe pourra toujours les attribuer) :
                </p>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {tables.map(t => (
                    <div key={t.id} className="flex items-center justify-between text-sm p-2 rounded border">
                      <span>{t.name}</span>
                      <span className="text-xs text-muted-foreground">{t.capacity} pers.</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {reservations.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-2">
                  {reservations.length} réservation{reservations.length > 1 ? 's' : ''} à venir sur la terrasse :
                </p>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {reservations.map(r => (
                    <div key={r.id} className="flex items-center justify-between text-sm p-2 rounded border">
                      <div>
                        <p className="font-medium">{r.customerName}</p>
                        <p className="text-xs text-muted-foreground">
                          {format(new Date(r.date), 'd MMM', { locale: fr })} à {r.startTime.substring(0, 5)} — {r.numberOfPeople} pers.
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex items-start gap-2 p-3 rounded-lg bg-orange-50 dark:bg-orange-950/30 text-xs text-orange-700 dark:text-orange-300 mt-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>Ces réservations restent sur leurs tables actuelles. Après désactivation, un encart « à replacer » apparaît dans la vue du jour pour les déplacer en intérieur en un clic.</span>
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button variant="destructive" onClick={() => { onConfirm(); onOpenChange(false); }}>
            Désactiver la terrasse
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
